import {useCallback, useEffect} from 'react';

import {useToast} from '@components/_context';
import {useEvent} from '@lookiero/event';
import {useIntl} from 'react-intl';
import {PaymentMethod} from '@lookiero/payments-front';

export type SuccessPayload = {message: {id: string; translation: string}; paymentMethod: PaymentMethod};
export type ErrorPayload = {error: {toaster?: {id: string}}};

export type HookParams = {
  closeErrorNotification?: boolean;
  disabled?: boolean;
  notifySuccess?: (a: SuccessPayload) => boolean;
  onSuccess?: (a: SuccessPayload) => void;
  onError?: (a: ErrorPayload) => void;
};

export const usePaymentInstrumentUpdateEvents = ({
  closeErrorNotification = true,
  disabled,
  notifySuccess = () => true,
  onSuccess = () => void 0,
  onError = () => void 0,
}: HookParams): void => {
  const {subscribe, unsubscribe} = useEvent();
  const {showError, showSuccess} = useToast();
  const {formatMessage} = useIntl();

  const onSuccessBase = useCallback(
    (payload: SuccessPayload) => {
      onSuccess(payload);
      if (notifySuccess(payload))
        showSuccess({
          message: payload.message.translation,
          settings: {nativeID: 'payment-instrument-success', testID: 'payment-instrument-success'},
        });
    },
    [notifySuccess, onSuccess, showSuccess],
  );

  const onErrorBase = useCallback(
    (payload: {error: {toaster?: {id: string}; translation?: string}}) => {
      onError(payload);
      showError({
        message: payload.error.translation || formatMessage({id: 'toast.generic.error'}),
        settings: !closeErrorNotification
          ? {nativeID: 'payment-instrument-update-rejected', timeoutClose: 0}
          : {nativeID: 'payment-instrument-update-rejected'},
      });
    },
    [closeErrorNotification, formatMessage, onError, showError],
  );

  useEffect(() => {
    if (disabled) return;

    subscribe({event: 'ERROR'}, onErrorBase);
    subscribe({event: 'PAYMENT_INSTRUMENT_UPDATED'}, onSuccessBase);
    subscribe({event: 'PAYMENT_INSTRUMENT_SELECTED_TO_DELAYED_HANDLING'}, onSuccessBase);
    return () => {
      unsubscribe({event: 'ERROR'}, onErrorBase);
      unsubscribe({event: 'PAYMENT_INSTRUMENT_UPDATED'}, onSuccessBase);
      unsubscribe({event: 'PAYMENT_INSTRUMENT_SELECTED_TO_DELAYED_HANDLING'}, onSuccessBase);
    };
  }, [disabled, onErrorBase, onSuccessBase, subscribe, unsubscribe]);
};
